import type { Mood } from "generator/components/mood";
import tinycolor from "tinycolor2";
import { moodColors } from "webapp/lib/theme/mood";
import type { RuntimeTheme } from "webapp/lib/theme/theme";

import { styled } from "../../../styled-components";

interface HintStyleProps {
    mood: Mood;
}

type MoodArgs = { mood: Mood; theme: RuntimeTheme };

function isDark(args: MoodArgs): boolean {
    return tinycolor(args.theme.baseColor).isDark();
}

function color({ mood, theme }: MoodArgs): tinycolor.Instance {
    const colors = moodColors(theme);
    return tinycolor(colors[mood]);
}

function fgColor(args: MoodArgs): tinycolor.Instance {
    const base = color(args);
    return isDark(args) ? base.desaturate(12).lighten(15) : base.darken(10);
}

function bgColor(args: MoodArgs): tinycolor.Instance {
    const base = color(args);
    return isDark(args) ? base.setAlpha(0.1).darken(0.1) : base.setAlpha(0.1);
}

export const HintStyle = styled.div<HintStyleProps>`
    --fg: ${p => fgColor(p).toRgbString()};
    --bg: ${p => bgColor(p).toRgbString()};
    --border-color: ${p =>
        isDark(p) ? fgColor(p).setAlpha(0.4).toRgbString() : fgColor(p).setAlpha(0.1).toRgbString()};

    padding: 12px;
    border-radius: 6px;
    border: 1px solid var(--border-color);
    margin: ${p => p.theme.componentSmallInnerVerticalPadding}px ${p => p.theme.pageMarginMedium}px;

    color: var(--fg);
    background-color: var(--bg);

    .hint-header {
        ${p => p.theme.fontStyles.Subheader5};
        font-weight: 700;
        margin-bottom: 4px;
    }
    .hint-body {
        ${p => p.theme.fontStyles.Body5};
        white-space: pre-wrap;
    }

    &.center {
        text-align: center;
    }

    &.right {
        text-align: right;
    }

    &.justify {
        text-align: justify;
    }
`;
